import { useState, useEffect, useRef } from "react";
import { apiFetch, getWsUrl } from "../utils/api";
import { getDiscordToken } from "../utils/discord";
import { unpackTerritoryDelta } from "../utils/packedDelta";

const sameOwner = (ownerId, currentUserId) =>
  ownerId != null &&
  currentUserId != null &&
  String(ownerId) === String(currentUserId);

// Numeric key encoding: (y << 16) | x — avoids string allocation/split
const mergeTerritory = (existing, delta) => {
  const next = new Set();
  if (existing && existing.x && existing.y) {
    for (let i = 0; i < existing.x.length; i++) {
      next.add((existing.y[i] << 16) | existing.x[i]);
    }
  }

  if (delta?.sub?.x?.length) {
    for (let i = 0; i < delta.sub.x.length; i++) {
      next.delete((delta.sub.y[i] << 16) | delta.sub.x[i]);
    }
  }

  if (delta?.add?.x?.length) {
    for (let i = 0; i < delta.add.x.length; i++) {
      next.add((delta.add.y[i] << 16) | delta.add.x[i]);
    }
  }

  const x = new Array(next.size);
  const y = new Array(next.size);
  let idx = 0;
  next.forEach((key) => {
    x[idx] = key & 0xFFFF;
    y[idx] = (key >> 16) & 0xFFFF;
    idx++;
  });
  return { x, y };
};

export function useGameState({
  id,
  userId,
  hasJoined,
  setHasJoined,
  isDiscord,
  navigate,
  config,
  // UI state setters that applyDeltaGameState needs
  setActionModal,
  setFoundingNation,
  setHasFounded,
  setIsSpectating,
  setIsDefeated,
  // Refs for reading current UI state without stale closures
  actionModalRef,
  isDefeatedRef,
  hasFoundedRef,
  isSpectatingRef,
  // Ref to handleEndGame to break circular dependency
  handleEndGameRef,
  // Current actionModal value for full-state polling dep
  actionModal,
  // Function to reset modes on id change
  exitModes,
  setSelectedCellInfo,
}) {
  const [gameState, setGameState] = useState(null);
  const [userState, setUserState] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState(null);
  const [combatFlashes, setCombatFlashes] = useState([]);
  const lastWsUpdateAtRef = useRef(0);

  const wsRef = useRef(null);
  const applyDeltaGameStateRef = useRef(null);
  const requestFullStateRef = useRef(null);
  const wsReconnectTimerRef = useRef(null);
  const wsReconnectAttemptsRef = useRef(0);
  const lastAppliedTickRef = useRef(-1);
  const pollRequestIdRef = useRef(0);
  const fullRequestIdRef = useRef(0);
  const pollInFlightRef = useRef(false);
  const fullInFlightRef = useRef(false);
  const pendingGapSyncRef = useRef(false);

  const applyDeltaGameState = (data, options = {}) => {
    const { isFullState = false } = options;
    if (!data?.gameState) return false;

    const incomingTick = Number(data.tickCount);
    const lastAppliedTick = Number(lastAppliedTickRef.current);
    if (Number.isFinite(incomingTick) && Number.isFinite(lastAppliedTick)) {
      if (!isFullState && incomingTick <= lastAppliedTick) {
        return false;
      }
      if (!isFullState && lastAppliedTick >= 0 && incomingTick > lastAppliedTick + 1) {
        if (!pendingGapSyncRef.current && requestFullStateRef.current) {
          pendingGapSyncRef.current = true;
          requestFullStateRef.current("tick-gap");
        }
        return false;
      }
    }

    // Unpack packed deltas if server is using that format
    if (data.usePackedDeltas && data.gameState?.nations) {
      data.gameState.nations = data.gameState.nations.map((nation) => {
        if (nation.packedDelta) {
          nation.territoryDeltaForClient = unpackTerritoryDelta(nation.packedDelta);
          delete nation.packedDelta;
        }
        return nation;
      });
    }

    const allowRefound = config?.territorial?.allowRefound !== false;
    const beginSpectate = () => {
      setActionModal(null);
      setFoundingNation(false);
      setHasFounded(false);
      setIsSpectating(true);
    };
    const beginRefound = () => {
      setActionModal(null);
      setFoundingNation(true);
      setHasFounded(false);
      setIsSpectating(false);
    };
    setGameState((prevState) => {
      const previousNations = prevState?.gameState?.nations || [];
      const prevTerritories = previousNations.reduce((acc, nation) => {
        acc[nation.owner] = nation.territory || null;
        return acc;
      }, {});

      if (data.gameState.nations) {
        const byOwner = new Map();
        data.gameState.nations.forEach((nation) => {
          const existing = byOwner.get(nation.owner);
          if (!existing) {
            byOwner.set(nation.owner, nation);
            return;
          }
          if (existing.status === "defeated" && nation.status !== "defeated") {
            byOwner.set(nation.owner, nation);
            return;
          }
          if (nation.status !== "defeated" && existing.status !== "defeated") {
            byOwner.set(nation.owner, nation);
          }
        });
        data.gameState.nations = Array.from(byOwner.values()).map((nation) => {
          const previousTerritory = prevTerritories[nation.owner] || null;
          if (nation.status === "defeated") {
            nation.territory = { x: [], y: [] };
            if (nation.territoryDeltaForClient) {
              delete nation.territoryDeltaForClient;
            }
            return nation;
          }
          if (nation.territoryDeltaForClient) {
            nation.territory = mergeTerritory(
              previousTerritory,
              nation.territoryDeltaForClient
            );
            // Keep territoryDeltaForClient — TerritoryRenderer uses it for incremental rendering
          }
          return nation;
        });
      }

      // Detect territory flips for combat flash effects
      if (!isFullState) {
        const now = performance.now();
        const newFlashes = [];
        data.gameState.nations.forEach((nation) => {
          if (!sameOwner(nation.owner, userId)) return;
          const delta = nation.territoryDeltaForClient;
          if (!delta) return;
          if (delta.add?.x?.length) {
            for (let i = 0; i < delta.add.x.length; i++) {
              newFlashes.push({ x: delta.add.x[i], y: delta.add.y[i], type: "capture", createdAt: now });
            }
          }
          if (delta.sub?.x?.length) {
            for (let i = 0; i < delta.sub.x.length; i++) {
              newFlashes.push({ x: delta.sub.x[i], y: delta.sub.y[i], type: "loss", createdAt: now });
            }
          }
        });
        if (newFlashes.length > 0) {
          setCombatFlashes((prev) => {
            if (prev.length === 0) return newFlashes.slice(-100);
            const combined = prev.length + newFlashes.length <= 100
              ? prev.concat(newFlashes)
              : prev.concat(newFlashes).slice(-100);
            return combined;
          });
        }
      }

      return {
        tickCount: data.tickCount,
        roomName: data.roomName,
        roomCreator: data.roomCreator,
        roomStatus: data.roomStatus || prevState?.roomStatus || "open",
        players: data.players || prevState?.players || [],
        gameState: data.gameState,
      };
    });
    if (Number.isFinite(incomingTick)) {
      lastAppliedTickRef.current = incomingTick;
    }
    if (isFullState) {
      pendingGapSyncRef.current = false;
    }

    const winningNation = data.gameState.nations?.find(
      (n) => n.status === "winner"
    );
    const winningLabel = winningNation
      ? winningNation.nationName || winningNation.displayName || winningNation.owner
      : null;
    if (winningNation) {
      if (sameOwner(winningNation.owner, userId)) {
        if (!actionModalRef.current || actionModalRef.current.type !== "win") {
          setActionModal({
            type: "win",
            message: "Congratulations! Your nation has won the game!",
            onClose: () => {
              handleEndGameRef.current?.();
            },
          });
        }
        setUserState(winningNation);
        setIsSpectating(false);
      } else {
        if (
          !actionModalRef.current ||
          actionModalRef.current.type !== "defeat"
        ) {
          setActionModal({
            type: "defeat",
            message: `${winningLabel} has won the game. Your nation has been defeated.`,
            onSpectate: beginSpectate,
          });
        }
        setUserState(null);
        setFoundingNation(false);
        setHasFounded(false);
        setIsDefeated(true);
        setIsSpectating(true);
      }
    } else {
      const playerNation = data.gameState.nations?.find(
        (n) => sameOwner(n.owner, userId) && n.status !== "defeated"
      );
      const defeatedNation = data.gameState.nations?.find(
        (n) => sameOwner(n.owner, userId) && n.status === "defeated"
      );
      const anyNationForUser = !!playerNation || !!defeatedNation;
      if (playerNation) {
        setUserState(playerNation);
        setIsDefeated(false);
        setHasFounded(true);
        setIsSpectating(false);
      } else {
        if (defeatedNation) {
          setIsDefeated(true);
          if (!allowRefound) {
            setIsSpectating(true);
          }
          if (!isDefeatedRef.current && hasFoundedRef.current) {
            setActionModal({
              type: "defeat",
              message: allowRefound
                ? "Your nation has been defeated! You can start over by founding a new nation or spectate."
                : "Your nation has been defeated. Refounding is disabled; you can spectate the match.",
              onSpectate: beginSpectate,
              onRefound: allowRefound ? beginRefound : null,
            });
          }
        } else {
          setIsDefeated(false);
          setIsSpectating(false);
        }
        const canInitialFound = !anyNationForUser;
        const canRefound = allowRefound && !!defeatedNation;
        const shouldSpectate =
          (!allowRefound && !!defeatedNation) || isSpectatingRef.current;
        setUserState(null);
        setFoundingNation((canInitialFound || canRefound) && !shouldSpectate);
        setHasFounded(false);
      }
    }
    return true;
  };

  useEffect(() => {
    lastAppliedTickRef.current = -1;
    pollRequestIdRef.current = 0;
    fullRequestIdRef.current = 0;
    pollInFlightRef.current = false;
    fullInFlightRef.current = false;
    pendingGapSyncRef.current = false;
    exitModes();
    setSelectedCellInfo(null);
  }, [id]);

  // Assign during render (not in useEffect) so the ref is available before any effects fire.
  applyDeltaGameStateRef.current = applyDeltaGameState;

  // WebSocket: subscribe to game state updates
  useEffect(() => {
    if (!id || !userId || !hasJoined) return;
    let isActive = true;

    const wsUrl = getWsUrl(isDiscord ? getDiscordToken() : undefined);

    const scheduleReconnect = () => {
      if (!isActive) return;
      const attempts = wsReconnectAttemptsRef.current;
      const delay = Math.min(10000, 500 * Math.pow(2, attempts));
      wsReconnectAttemptsRef.current = attempts + 1;
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
      }
      wsReconnectTimerRef.current = setTimeout(() => {
        if (isActive) connect();
      }, delay);
    };

    const connect = () => {
      if (!isActive) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Connection timeout: if onopen doesn't fire within 10s, close and reconnect
      const connectTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.warn("[WS] connection timeout, closing");
          ws.close();
        }
      }, 10000);

      ws.onopen = () => {
        clearTimeout(connectTimeout);
        console.log("[WS] open");
        wsReconnectAttemptsRef.current = 0;
        ws.send(
          JSON.stringify({
            type: "subscribe",
            roomId: id,
          })
        );
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "subscribed") {
            setWsConnected(true);
            return;
          }
          if (msg.type === "state") {
            lastWsUpdateAtRef.current = Date.now();
            applyDeltaGameStateRef.current?.(msg);
            return;
          }
          if (msg.type === "error") {
            setError(msg.message || "WebSocket error");
            ws.close();
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };

      ws.onclose = () => {
        clearTimeout(connectTimeout);
        console.warn("[WS] closed");
        setWsConnected(false);
        scheduleReconnect();
      };
      ws.onerror = (err) => {
        clearTimeout(connectTimeout);
        console.warn("[WS] error", err);
        setWsConnected(false);
        ws.close();
      };
    };

    connect();

    // Stale connection detector: if no message received for 45s, close and reconnect
    const staleCheckInterval = setInterval(() => {
      if (!isActive || !wsRef.current) return;
      const ws = wsRef.current;
      if (ws.readyState !== WebSocket.OPEN) return;
      const timeSinceLastMsg = Date.now() - (lastWsUpdateAtRef.current || 0);
      if (lastWsUpdateAtRef.current && timeSinceLastMsg > 45000) {
        console.warn("[WS] stale connection detected, reconnecting");
        ws.close();
      }
    }, 15000);

    return () => {
      isActive = false;
      clearInterval(staleCheckInterval);
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
      }
      wsRef.current?.close();
    };
  }, [id, userId, hasJoined]);

  // Poll game state (every 200ms or 2s when WS connected)
  useEffect(() => {
    if (!userId || !hasJoined) return;

    const fetchGameState = async () => {
      if (
        wsConnected &&
        Date.now() - lastWsUpdateAtRef.current < 1000
      ) {
        return;
      }
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      const requestId = ++pollRequestIdRef.current;
      try {
        const response = await apiFetch(`api/gamerooms/${id}/state`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });

        if (!response.ok) {
          if (response.status === 403) {
            setHasJoined(false);
            return;
          }
          navigate("/rooms");
          return;
        }

        const data = await response.json();
        if (requestId !== pollRequestIdRef.current) {
          return;
        }

        applyDeltaGameState(data);
      } catch (err) {
        console.error("Error fetching game state:", err);
      } finally {
        pollInFlightRef.current = false;
      }
    };

    fetchGameState();
    const intervalMs = wsConnected ? 2000 : 250;
    const interval = setInterval(fetchGameState, intervalMs);
    return () => clearInterval(interval);
  }, [id, userId, hasJoined, navigate, wsConnected]);

  // Full state polling effect: every 5 seconds (or 15s when WS connected)
  useEffect(() => {
    if (!userId || !hasJoined) return;

    const fetchFullState = async () => {
      if (fullInFlightRef.current) return;
      fullInFlightRef.current = true;
      const requestId = ++fullRequestIdRef.current;

      // If an action modal is active (win/defeat popup), skip full state update.
      if (actionModal) {
        fullInFlightRef.current = false;
        pendingGapSyncRef.current = false;
        return;
      }

      try {
        const response = await apiFetch(`api/gamerooms/${id}/state`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            full: true,
          }),
        });
        if (!response.ok) {
          if (response.status === 403) {
            setHasJoined(false);
            return;
          }
          navigate("/rooms");
          return;
        }
        const data = await response.json();
        if (requestId !== fullRequestIdRef.current) {
          return;
        }
        applyDeltaGameState(data, { isFullState: true });
      } catch (err) {
        console.error("Error fetching full game state:", err);
      } finally {
        if (requestId === fullRequestIdRef.current) {
          pendingGapSyncRef.current = false;
        }
        fullInFlightRef.current = false;
      }
    };

    requestFullStateRef.current = fetchFullState;

    // Immediately fetch full state on mounting
    fetchFullState();
    // Then set interval for rectification every 5 seconds
    const fullIntervalMs = wsConnected ? 15000 : 1000;
    const interval = setInterval(fetchFullState, fullIntervalMs);
    return () => {
      clearInterval(interval);
      if (requestFullStateRef.current === fetchFullState) {
        requestFullStateRef.current = null;
      }
    };
  }, [id, userId, hasJoined, navigate, actionModal, wsConnected]);

  return {
    gameState,
    setGameState,
    userState,
    setUserState,
    wsConnected,
    paused,
    setPaused,
    error,
    setError,
    combatFlashes,
    setCombatFlashes,
    lastWsUpdateAtRef,
    applyDeltaGameStateRef,
    requestFullStateRef,
  };
}
